/**
 * The levers a character build may pull, and the only ones.
 *
 * Every field multiplies exactly one named quantity somewhere else in the sim, and the identity is
 * one. A field is named for the quantity it multiplies, never for the benefit: `reloadSeconds`
 * rather than `reloadSpeed`, so a contribution of `0.9` reads as "a tenth off the reload" without
 * anyone having to decide again whether lower is better. `betterWhen` exists for the screen, which
 * has to colour the arrow, and for nothing in the simulation.
 *
 * The closed union follows `AffixField` rather than the registry pattern: a field is three pieces
 * of metadata, and a directory of three-line files would be a list wearing a category's clothes.
 * The instances that do keep growing are the perks, and those are files.
 *
 * Bounds are not decoration. Multipliers stack multiplicatively, so without a floor a stack of
 * reload perks reaches zero and a reload becomes instant, and without a ceiling a stack of carry
 * perks makes weight meaningless. Clamping at the field is what stops every consumer clamping.
 */

/** Every lever in the game. Explicit, so a field used but not declared fails to compile. */
export type EffectFieldId =
  | 'reloadSeconds'
  | 'adsSeconds'
  | 'weaponSpread'
  | 'criticalChance'
  | 'incomingDamage'
  | 'bleedoutSeconds'
  | 'reviveSeconds'
  | 'moveSpeed'
  | 'interactSeconds'
  | 'lootQuality'
  | 'carryKg';

/** Aggregation order, and part of the determinism contract. */
export const EFFECT_FIELD_ORDER: readonly EffectFieldId[] = [
  'reloadSeconds',
  'adsSeconds',
  'weaponSpread',
  'criticalChance',
  'incomingDamage',
  'bleedoutSeconds',
  'reviveSeconds',
  'moveSpeed',
  'interactSeconds',
  'lootQuality',
  'carryKg',
];

export interface EffectField {
  readonly id: EffectFieldId;
  /** What the screen prints. The quantity, not the benefit. */
  readonly label: string;
  /** Which way is good news, for the arrow on the upgrade screen and nothing else. */
  readonly betterWhen: 'higher' | 'lower';
  /** The tightest a stack of everything may ever squeeze this quantity. */
  readonly min: number;
  readonly max: number;
}

export const EFFECT_FIELDS = {
  reloadSeconds: {
    id: 'reloadSeconds',
    label: 'Reload time',
    betterWhen: 'lower',
    min: 0.5,
    max: 2,
  },
  adsSeconds: { id: 'adsSeconds', label: 'Time to aim', betterWhen: 'lower', min: 0.5, max: 2 },
  weaponSpread: { id: 'weaponSpread', label: 'Spread', betterWhen: 'lower', min: 0.6, max: 2 },
  criticalChance: {
    id: 'criticalChance',
    label: 'Critical chance',
    betterWhen: 'higher',
    min: 0.5,
    max: 2,
  },
  incomingDamage: {
    id: 'incomingDamage',
    label: 'Damage taken',
    betterWhen: 'lower',
    min: 0.4,
    max: 2,
  },
  bleedoutSeconds: {
    id: 'bleedoutSeconds',
    label: 'Time to bleed out',
    betterWhen: 'higher',
    min: 0.5,
    max: 2,
  },
  reviveSeconds: {
    id: 'reviveSeconds',
    label: 'Time to pick someone up',
    betterWhen: 'lower',
    min: 0.4,
    max: 2,
  },
  moveSpeed: { id: 'moveSpeed', label: 'Movement speed', betterWhen: 'higher', min: 0.7, max: 1.5 },
  interactSeconds: {
    id: 'interactSeconds',
    label: 'Time to open something',
    betterWhen: 'lower',
    min: 0.4,
    max: 2,
  },
  lootQuality: { id: 'lootQuality', label: 'Loot quality', betterWhen: 'higher', min: 0.5, max: 2 },
  carryKg: { id: 'carryKg', label: 'Carry capacity', betterWhen: 'higher', min: 0.5, max: 2 },
} as const satisfies Record<EffectFieldId, EffectField>;

/** Field lookup, total over the union so it never widens to `undefined`. */
export function effectField(id: EffectFieldId): EffectField {
  return EFFECT_FIELDS[id];
}

/** Whether `value` names a lever. Used when reading a perk effect back out of a save or a test. */
export function isEffectFieldId(value: string): value is EffectFieldId {
  return Object.hasOwn(EFFECT_FIELDS, value);
}
